import { createElement as _createElement } from "react";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import Divider from "@mui/material/Divider";
import InputAdornment from "@mui/material/InputAdornment";
import { Observer, observer } from "mobx-react";
import * as React from "react";
import { addDisplayName } from "../Infra/display-name";
import { AutocompleteMenuItem, MenuCheck, StyledAutocomplete, StyledAutocompletePopper } from "./HXAutocomplete.styles";
import { HXAutocompleteViewModel } from "./HXAutocompleteViewModel";
import { HXTextField } from "./base.styles";
import { setInputValue } from "./reactutils";
import { useViewModel } from "./useViewModel";
export function noOptionsMessage(opts) {
    var _a, _b, _c;
    const minLength = (_a = opts.minLengthBeforeSearch) !== null && _a !== void 0 ? _a : 3;
    if (opts.usesAsyncSearch && minLength > 0 && opts.inputValue.length < minLength) {
        return (_b = opts.minLengthNoOptionsText) !== null && _b !== void 0 ? _b : `Type at least ${minLength} characters to search.`;
    }
    return (_c = opts.noOptionsText) !== null && _c !== void 0 ? _c : "No matches found";
}
export const HXAutocomplete = observer((props) => {
    var _a, _b, _c, _d;
    const opts = { loadOptions: props.loadOptions, options: props.options, minLengthBeforeSearch: props.minLengthBeforeSearch };
    const model = useViewModel((HXAutocompleteViewModel), opts);
    const previousProps = React.useRef(undefined);
    React.useLayoutEffect(() => {
        model.updateOpts(opts);
    }, [props.options]);
    previousProps.current = props;
    const [open, setOpen] = React.useState((_a = props.defaultOpen) !== null && _a !== void 0 ? _a : false);
    return (_jsx(StyledAutocomplete, { disabled: props.disabled === true, className: props.className, filterSelectedOptions: !props.showSelectedValueInOptions, filterOptions: props.loadOptions ? (option) => option : undefined, options: props.value && !props.showSelectedValueInOptions ? [...model.options, props.value] : model.options, value: props.value, onChange: (_event, newValue) => props.onChange(newValue), onInputChange: (_event, newInputValue) => {
            var _a;
            model.setInputValue(newInputValue);
            (_a = props.onInputChange) === null || _a === void 0 ? void 0 : _a.call(props, newInputValue);
        }, renderInput: (params) => {
            var _a, _b;
            if (props.leftIcon !== undefined) {
                params.InputProps.startAdornment = (_jsxs(_Fragment, { children: [_jsx(InputAdornment, { position: "start", children: props.leftIcon }), params.InputProps.startAdornment] }));
            }
            if (props.readOnlyTextField)
                params.inputProps.readOnly = true;
            const inputOnKeyDown = params.inputProps.onKeyDown;
            const inputOnPaste = params.inputProps.onPaste;
            params.inputProps.onKeyDown = (event) => {
                var _a;
                inputOnKeyDown === null || inputOnKeyDown === void 0 ? void 0 : inputOnKeyDown(event);
                (_a = props.onInputKeyDown) === null || _a === void 0 ? void 0 : _a.call(props, event);
            };
            params.inputProps.onPaste = (event) => {
                var _a;
                inputOnPaste === null || inputOnPaste === void 0 ? void 0 : inputOnPaste(event);
                (_a = props.onInputPaste) === null || _a === void 0 ? void 0 : _a.call(props, event);
            };
            return (_jsx(HXTextField, { autoFocus: (_a = props.defaultOpen) !== null && _a !== void 0 ? _a : false, ...params, label: props.label, placeholder: props.placeholder, error: props.error, required: (_b = props.required) !== null && _b !== void 0 ? _b : false, helperText: props.helperText }));
        }, PopperComponent: (_b = props.PopperComponent) !== null && _b !== void 0 ? _b : StyledAutocompletePopper, componentsProps: { popper: { placement: "bottom-end", ...(props.menuWidth ? { style: { width: "400px" } } : {}) } }, getOptionLabel: (option) => props.getDisplayValue(option), renderOption: (cprops, option) => {
            var _a;
            const onClick = cprops["aria-disabled"] === true ? undefined : cprops.onClick;
            return (_jsxs(_Fragment, { children: [((_a = props.menuItemPrecededByDivider) === null || _a === void 0 ? void 0 : _a.call(props, option)) && _jsx(Divider, {}), _createElement(AutocompleteMenuItem, { ...cprops, key: props.getId(option), onClick: onClick },
                        props.getMenuItem ? props.getMenuItem(option) : props.getDisplayValue(option),
                        props.showSelectedValueInOptions && option === props.value && _jsx(MenuCheck, {}))] }));
        }, isOptionEqualToValue: (option, value) => props.getId(option) === props.getId(value), noOptionsText: noOptionsMessage({
            inputValue: model.inputValue,
            usesAsyncSearch: props.loadOptions !== undefined,
            minLengthBeforeSearch: props.minLengthBeforeSearch,
            minLengthNoOptionsText: props.minLengthNoOptionsText,
            noOptionsText: props.noOptionsText,
        }), style: props.style, groupBy: props.groupBy, onOpen: () => setOpen(true), onClose: (_event, reason) => {
            var _a;
            setOpen(false);
            (_a = props.onClose) === null || _a === void 0 ? void 0 : _a.call(props, reason);
        }, open: open, disableClearable: (_c = props.disableClear) !== null && _c !== void 0 ? _c : false, forcePopupIcon: (_d = props.forcePopupIcon) !== null && _d !== void 0 ? _d : true, getOptionDisabled: props.getOptionDisabled }));
});
addDisplayName(HXAutocomplete, "HXAutocomplete");
export const HXAutocompleteMulti = observer((props) => {
    var _a, _b, _c;
    const model = useViewModel((HXAutocompleteViewModel), {
        loadOptions: props.loadOptions,
        options: props.options,
        minLengthBeforeSearch: props.minLengthBeforeSearch,
    });
    const [open, setOpen] = React.useState((_a = props.defaultOpen) !== null && _a !== void 0 ? _a : false);
    return (_jsx(StyledAutocomplete, { disabled: props.disabled === true, className: props.className, multiple: true, filterSelectedOptions: true, filterOptions: props.loadOptions ? (option) => option : undefined, options: [...model.options, ...props.value], value: props.value, onChange: (_event, newValue) => props.onChange(newValue), onInputChange: (_event, newInputValue) => {
            var _a;
            model.setInputValue(newInputValue);
            (_a = props.onInputChange) === null || _a === void 0 ? void 0 : _a.call(props, newInputValue);
        }, renderInput: (params) => {
            var _a, _b;
            return (_jsx(HXTextField, { ...params, autoFocus: (_a = props.defaultOpen) !== null && _a !== void 0 ? _a : false, label: props.label, placeholder: props.placeholder, error: props.error, required: (_b = props.required) !== null && _b !== void 0 ? _b : false, helperText: props.helperText }));
        }, PopperComponent: (_b = props.PopperComponent) !== null && _b !== void 0 ? _b : StyledAutocompletePopper, componentsProps: { popper: { placement: "bottom-end", ...(props.menuWidth ? { style: { width: "400px" } } : {}) } }, getOptionLabel: (option) => props.getDisplayValue(option), renderOption: (cprops, option) => (_createElement(AutocompleteMenuItem, { ...cprops, key: props.getId(option) }, props.getMenuItem ? props.getMenuItem(option) : props.getDisplayValue(option))), isOptionEqualToValue: (option, value) => props.getId(option) === props.getId(value), noOptionsText: noOptionsMessage({
            inputValue: model.inputValue,
            usesAsyncSearch: props.loadOptions !== undefined,
            minLengthBeforeSearch: props.minLengthBeforeSearch,
            minLengthNoOptionsText: props.minLengthNoOptionsText,
            noOptionsText: props.noOptionsText,
        }), style: props.style, groupBy: props.groupBy, size: "small", onOpen: () => setOpen(true), onClose: (_event, reason) => {
            var _a;
            setOpen(false);
            (_a = props.onClose) === null || _a === void 0 ? void 0 : _a.call(props, reason);
        }, open: open, disableClearable: (_c = props.disableClear) !== null && _c !== void 0 ? _c : false, renderTags: props.renderTags
            ? (values, getTagProps) => _jsx(Observer, { children: () => _jsx(_Fragment, { children: props.renderTags(values, getTagProps) }) })
            : undefined }));
});
addDisplayName(HXAutocompleteMulti, "HXAutocompleteMulti");
export const HXTextFieldMulti = observer((props) => {
    const emptyOptions = [];
    const delimeterRegex = props.delimiterCharacters ? new RegExp(`[${props.delimiterCharacters.join("")}]`) : null;
    return (_jsx(StyledAutocomplete, { disabled: props.disabled === true, className: props.className, multiple: true, freeSolo: true, autoSelect: true, size: "small", options: emptyOptions, value: props.value, inputValue: props.inputValue, onChange: (_event, newValue) => {
            const delimiterResolvedValue = newValue.flatMap((item) => {
                if (delimeterRegex) {
                    return item.split(delimeterRegex);
                }
                else {
                    return [item];
                }
            });
            props.onChange(delimiterResolvedValue);
        }, onInputChange: (_event, newInputValue) => {
            var _a;
            (_a = props.onInputChange) === null || _a === void 0 ? void 0 : _a.call(props, newInputValue);
        }, open: false, renderInput: (params) => {
            params.inputProps.onKeyDown = (event) => {
                var _a;
                if ((_a = props.delimiterCharacters) === null || _a === void 0 ? void 0 : _a.includes(event.key)) {
                    event.preventDefault();
                    event.stopPropagation();
                    const newValue = event.currentTarget.value;
                    if (newValue.length > 0) {
                        props.onChange([...props.value, newValue]);
                        setInputValue(event.currentTarget, "");
                    }
                }
            };
            return (_jsx(HXTextField, { ...params, label: props.label, placeholder: props.placeholder, error: props.error, onFocus: () => (props.onFocus ? props.onFocus(true) : null), onBlur: () => (props.onFocus ? props.onFocus(false) : null), helperText: props.helperText, required: props.required, multiline: props.multiline, rows: props.multiline ? 6 : undefined }));
        }, style: { flex: "1 0 0", ...props.style }, renderTags: props.renderTags
            ? (values, getTagProps) => _jsx(Observer, { children: () => _jsx(_Fragment, { children: props.renderTags(values, getTagProps) }) })
            : undefined }));
});
addDisplayName(HXTextFieldMulti, "HXTextFieldMulti");
